if (!customElements.get("avaliability-notification-drawer")) {
  customElements.define(
    "avaliability-notification-drawer",
    class AvaliabilityNotificationDrawer extends HTMLElement {
      constructor() {
        super();

        const closeElementsSelectors = [
          ".avaliability-notification-drawer-close",
          ".avaliability-notification-overlay",
        ];

        this.querySelectorAll(closeElementsSelectors).forEach((closeEl) => {
          closeEl.addEventListener("click", () => {
            this.hide();
          });
        });

        this.openBtn = document.querySelector(
          ".avaliability-notification-open-btn"
        );

        this.openBtn?.addEventListener("click", (e) => {
          this.show(e.target);
        });

        this.variantChangeUnsubscriber = undefined;

        this.form = this.querySelector(".avaliability-notification-form");
        this.form?.addEventListener("submit", (event) => this.onSubmit(event));
      }

      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
        this.setOpenBtnStatus();

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const currentVariant = event.data.variant;
            this.resetStatus();
            this.updateInfo(currentVariant);
          }
        );
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      hide() {
        this.removeAttribute("open");
        this.classList.remove("modal--open");
        document.body.classList.remove("overflow-hidden");
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute("open", "");
        this.classList.add("modal--open");
        document.body.classList.add("overflow-hidden");

        this.addEventListener(
          "transitionend",
          () => {
            trapFocus(this, this.querySelector('[role="dialog"]'));
          },
          { once: true }
        );
      }

      setOpenBtnStatus() {
        const params = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        if (
          params.get("contact_posted") === "true" &&
          hash.includes("#AvaliabilityNotificationForm-")
        ) {
          const match = hash.match(/#(AvaliabilityNotificationForm-[\w\-]+)/);
          if (match && match[1]) {
            const form_id = match[1];
            const buttonId = `Button-${form_id}`;
            const button = document.getElementById(buttonId);

            if (button) {
              button.dataset.status = "success";
            }
          }
        }
      }

      updateInfo(currentVariant) {
        const formattedOptions =
          currentVariant.options.length > 1
            ? currentVariant.options.join(" / ")
            : currentVariant.options[0] || "";

        const optionsEl = this.querySelector(
          ".avaliability-notification-product-options"
        );
        const inputOptions = this.querySelector(
          "#AvaliabilityNotification-options"
        );

        if (optionsEl) optionsEl.textContent = formattedOptions;
        if (inputOptions) inputOptions.value = formattedOptions;

        const inputVariant = this.querySelector(
          "#AvaliabilityNotification-variant"
        );

        if (inputVariant) inputVariant.value = currentVariant.id;

        const inputReturn = this.querySelector(
          "#AvaliabilityNotification-return"
        );

        if (inputReturn && inputReturn.value.includes("variant=")) {
          const newUrl = inputReturn.value.replace(
            /variant=\d+/,
            `variant=${currentVariant.id}`
          );
          inputReturn.value = newUrl;
        }
      }

      /**
       * Registers the sign-up in Klaviyo instead of posting the native contact
       * form. Klaviyo then sends the restock email automatically.
       *
       * If anything fails — no public key configured, network error, Klaviyo
       * rejects the payload — we deliberately fall through to the theme's
       * native contact-form submission, so the request still reaches the
       * merchant by email rather than being silently lost.
       */
      async onSubmit(event) {
        const companyId = this.dataset.klaviyoCompanyId;
        const email = this.querySelector(
          "#AvaliabilityNotification-Email"
        )?.value.trim();
        const variantId = this.querySelector(
          "#AvaliabilityNotification-variant"
        )?.value;

        if (!companyId || !email || !variantId) return;

        event.preventDefault();

        const submitBtn = this.querySelector('button[type="submit"]');
        submitBtn?.classList.add("loading");

        try {
          const subscribed = await this.subscribeToKlaviyo(
            companyId,
            email,
            variantId
          );

          if (!subscribed) throw new Error("Klaviyo subscription refused");

          // Name / phone are optional fields; when the merchant has enabled
          // them they go on the profile in a separate call, because the
          // back-in-stock endpoint accepts neither. A failure here must not
          // lose the subscription we just registered, so it is not awaited
          // into the catch below.
          this.updateKlaviyoProfile(companyId, email);

          this.showSuccess();
        } catch (error) {
          console.error("[avaliability-notification]", error);
          submitBtn?.classList.remove("loading");
          this.form?.submit();
        }
      }

      async subscribeToKlaviyo(companyId, email, variantId) {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
            companyId
          )}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2026-07-15",
            },
            credentials: "omit",
            body: JSON.stringify({
              data: {
                type: "back-in-stock-subscription",
                attributes: {
                  profile: {
                    data: { type: "profile", attributes: { email: email } },
                  },
                  channels: ["EMAIL"],
                },
                relationships: {
                  variant: {
                    data: {
                      type: "catalog-variant",
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        return response.ok;
      }

      async updateKlaviyoProfile(companyId, email) {
        const name = this.querySelector(
          "#AvaliabilityNotification-Name"
        )?.value.trim();
        const phone = this.querySelector(
          "#AvaliabilityNotification-Phone"
        )?.value.trim();

        if (!name && !phone) return;

        const attributes = { email: email };

        if (name) {
          const parts = name.split(" ").filter(Boolean);
          attributes.first_name = parts.shift();
          if (parts.length) attributes.last_name = parts.join(" ");
        }

        // Klaviyo requires E.164; anything else is rejected, so skip it rather
        // than have it fail the whole profile update.
        if (phone && /^\+[0-9]{6,20}$/.test(phone)) {
          attributes.phone_number = phone;
        }

        try {
          await fetch(
            `https://a.klaviyo.com/client/profiles?company_id=${encodeURIComponent(
              companyId
            )}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                revision: "2026-07-15",
              },
              credentials: "omit",
              body: JSON.stringify({
                data: { type: "profile", attributes: attributes },
              }),
            }
          );
        } catch (error) {
          console.error("[avaliability-notification] profile update", error);
        }
      }

      showSuccess() {
        const submitBtn = this.querySelector('button[type="submit"]');
        submitBtn?.classList.remove("loading");

        const wrapper = this.querySelector(
          ".avaliability-notification-form-wrapper"
        );
        const success = this.querySelector(
          "[data-avaliability-notification-success]"
        );

        // Use the theme's .hidden utility, not the [hidden] attribute: both
        // .form__message and .avaliability-notification-form-wrapper set
        // `display`, which beats the user-agent [hidden] rule.
        if (wrapper) wrapper.classList.add("hidden");
        if (success) success.classList.remove("hidden");

        if (this.openBtn) this.openBtn.dataset.status = "success";
      }

      resetStatus() {
        // Keep our own success node — it is reused on every submit, so removing
        // it would leave a later sign-up with no confirmation to show.
        const messages = this.querySelectorAll(
          ".form__message:not([data-avaliability-notification-success])"
        );
        messages.forEach((el) => el.remove());

        const success = this.querySelector(
          "[data-avaliability-notification-success]"
        );

        if (success) success.classList.add("hidden");

        const wrapper = this.querySelector(
          ".avaliability-notification-form-wrapper"
        );

        if (wrapper) wrapper.classList.remove("hidden");

        if (this.openBtn) {
          this.openBtn.dataset.status = "false";
        }
      }
    }
  );
}
